import { Controller } from "@hotwired/stimulus"

// Reusable bulk-select controller for any table or list with checkboxes.
// Shows/hides an action bar and populates hidden form inputs with selected IDs.
// Handles responsive layouts where duplicate checkboxes exist (e.g. desktop table + mobile cards)
// by only counting visible checkboxes.
//
// Targets:
//   selectAll     - "Select all" checkbox in the table header (optional)
//   checkbox      - Individual row checkboxes (must have a `value` attribute)
//   bar           - Bulk action bar container (toggled via `hidden` class)
//   count         - Element whose textContent is set to the selected count
//   idsContainer  - Container where hidden <input> fields are injected for form submission
//
// Values:
//   paramName (String, default: "ids[]") - The form field name for selected IDs.
//     Set via data-bulk-select-param-name-value, e.g. "submission_ids[]", "invoice_ids[]"
//
// Example:
//   <div data-controller="bulk-select" data-bulk-select-param-name-value="invoice_ids[]">
//     <div data-bulk-select-target="bar" class="hidden">
//       <span data-bulk-select-target="count">0</span> selected
//       <form method="post" action="/bulk_action">
//         <div data-bulk-select-target="idsContainer"></div>
//         <button type="submit">Delete Selected</button>
//       </form>
//     </div>
//     <table>
//       <thead>
//         <tr>
//           <th><input type="checkbox" data-bulk-select-target="selectAll" data-action="change->bulk-select#toggleAll" /></th>
//         </tr>
//       </thead>
//       <tbody>
//         <tr>
//           <td><input type="checkbox" data-bulk-select-target="checkbox" data-action="change->bulk-select#update" value="123" /></td>
//         </tr>
//       </tbody>
//     </table>
//   </div>
export default class extends Controller {
  static targets = ["selectAll", "checkbox", "bar", "count", "idsContainer"]
  static values = { paramName: { type: String, default: "ids[]" } }

  connect() {
    this.update()
  }

  get visibleCheckboxes() {
    return this.checkboxTargets.filter(cb => cb.offsetParent !== null)
  }

  toggleAll() {
    const checked = this.selectAllTarget.checked
    this.visibleCheckboxes.forEach(cb => cb.checked = checked)
    this.update()
  }

  update() {
    const visible = this.visibleCheckboxes
    const checked = visible.filter(cb => cb.checked)

    if (checked.length > 0) {
      this.barTarget.classList.remove("hidden")
      this.countTarget.textContent = checked.length
    } else {
      this.barTarget.classList.add("hidden")
    }

    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = visible.length > 0 && checked.length === visible.length
      this.selectAllTarget.indeterminate = checked.length > 0 && checked.length < visible.length
    }

    const seen = new Set()
    this.idsContainerTarget.innerHTML = ""
    checked.forEach(cb => {
      if (seen.has(cb.value)) return
      seen.add(cb.value)
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = this.paramNameValue
      input.value = cb.value
      this.idsContainerTarget.appendChild(input)
    })
  }
}
